// @ts-check

import { html } from "lit-html";
import { renderInputField, renderPlaceholder, renderSectionCard, renderTimeResultBlock, renderErrorMessage } from "../ui.js";
import { hasText, parseNumber } from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createTimestampState>} TimestampState */
/** @typedef {{ kind: "empty" } | { kind: "error", error: string } | { kind: "ready", utc: string, spain: string, newYork: string, local: string }} TimestampConversionResult */
/** @typedef {{ kind: "empty" } | { kind: "error", error: string } | { kind: "ready", timestamp: string }} DateToTimestampResult */

export const timestampTool = {
  key: "timestamp",
  path: "/timestamp",
  title: "Timestamp",
  blurb: "Convert Unix timestamps to dates and build UTC timestamps from dates.",
  badge: "Time",
  accentClass: "bg-gradient-to-r from-lime-300 via-emerald-300 to-teal-300",
  createState: createTimestampState,
  render: renderTimestampPage,
  /**
   * @param {TimestampState} toolState
   * @returns {boolean}
   */
  shouldAutoRefresh: function (toolState) {
    return hasText(toolState.timestamp);
  },
};

function createTimestampState() {
  return {
    timestamp: "",
    year: "",
    month: "",
    day: "",
    hour: "",
    minute: "",
  };
}

/**
 * @param {TimestampState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderTimestampPage(toolState, env) {
  const timestampResult = computeTimestampConversionResult(toolState.timestamp);
  const dateToTimestampResult = computeDateToTimestampResult(
    toolState.year,
    toolState.month,
    toolState.day,
    toolState.hour,
    toolState.minute,
  );

  return html`
    <section class="grid gap-4 xl:grid-cols-2 xl:items-start">
      ${renderSectionCard(
        "Unix timestamp to date",
        "Paste seconds or milliseconds and compare the timestamp across a few useful timezones.",
        html`
          ${renderInputField({
            label: "Unix timestamp",
            value: toolState.timestamp,
            type: "text",
            step: "any",
            placeholder: "1646328000",
            onInput: function (event) {
              env.handleFieldInput(toolState, "timestamp", event);
            },
          })}
          <div class="mt-4">${renderTimestampConversionResult(timestampResult)}</div>
        `,
      )}

      ${renderSectionCard(
        "Date to Unix timestamp",
        "Build a UTC timestamp from date parts. Hour and minute are optional.",
        html`
          <div class="grid gap-4 grid-cols-3">
            ${renderInputField({
              label: "Year",
              value: toolState.year,
              placeholder: "2026",
              step: "1",
              onInput: function (event) {
                env.handleFieldInput(toolState, "year", event);
              },
            })}
            ${renderInputField({
              label: "Month",
              value: toolState.month,
              placeholder: "5",
              step: "1",
              onInput: function (event) {
                env.handleFieldInput(toolState, "month", event);
              },
            })}
            ${renderInputField({
              label: "Day",
              value: toolState.day,
              placeholder: "29",
              step: "1",
              onInput: function (event) {
                env.handleFieldInput(toolState, "day", event);
              },
            })}
          </div>

          <div class="mt-4 grid gap-4 sm:grid-cols-2">
            ${renderInputField({
              label: "Hour (optional)",
              value: toolState.hour,
              placeholder: "14",
              step: "1",
              onInput: function (event) {
                env.handleFieldInput(toolState, "hour", event);
              },
            })}
            ${renderInputField({
              label: "Minute (optional)",
              value: toolState.minute,
              placeholder: "30",
              step: "1",
              onInput: function (event) {
                env.handleFieldInput(toolState, "minute", event);
              },
            })}
          </div>

          <div class="mt-4">${renderDateToTimestampResult(dateToTimestampResult)}</div>
        `,
      )}
    </section>
  `;
}

/**
 * @param {TimestampConversionResult} result
 * @returns {LitView}
 */
function renderTimestampConversionResult(result) {
  if (result.kind === "empty") {
    return renderPlaceholder("Enter a timestamp in seconds or milliseconds.");
  }

  if (result.kind === "error") {
    return renderErrorMessage(result.error);
  }

  return html`
    <div class="grid gap-3">
      ${renderTimeResultBlock("UTC time", result.utc)}
      ${renderTimeResultBlock("Spain time (Europe/Madrid)", result.spain)}
      ${renderTimeResultBlock("New York time", result.newYork)}
      ${renderTimeResultBlock("Your local time", result.local)}
    </div>
  `;
}

/**
 * @param {DateToTimestampResult} result
 * @returns {LitView}
 */
function renderDateToTimestampResult(result) {
  if (result.kind === "empty") {
    return renderPlaceholder("Fill at least year, month, and day to generate a UTC timestamp.");
  }

  if (result.kind === "error") {
    return renderErrorMessage(result.error);
  }

  return html`
    <div class="rounded-lg bg-stone-100 px-4 py-4">
      <div class="text-xs font-semibold uppercase tracking-widest text-stone-400">Unix timestamp</div>
      <div class="mt-3 text-3xl font-semibold tracking-tight text-stone-900">${result.timestamp}</div>
    </div>
  `;
}

/**
 * @param {string} timestampText
 * @returns {TimestampConversionResult}
 */
function computeTimestampConversionResult(timestampText) {
  if (!hasText(timestampText)) {
    return { kind: "empty" };
  }

  const timestampValue = parseNumber(timestampText);
  if (!Number.isFinite(timestampValue)) {
    return { kind: "error", error: "Please enter a valid number." };
  }

  const timestampMs = Math.abs(timestampValue) >= 1e10
    ? timestampValue
    : timestampValue * 1000;
  const date = new Date(timestampMs);
  if (Number.isNaN(date.getTime())) {
    return { kind: "error", error: "Invalid timestamp." };
  }

  return {
    kind: "ready",
    utc: formatTimestampDate(date, "UTC"),
    spain: formatTimestampDate(date, "Europe/Madrid"),
    newYork: formatTimestampDate(date, "America/New_York"),
    local: `${formatTimestampDate(date, undefined)} (${getRelativeTimeLabel(date)})`,
  };
}

/**
 * @param {string} yearText
 * @param {string} monthText
 * @param {string} dayText
 * @param {string} hourText
 * @param {string} minuteText
 * @returns {DateToTimestampResult}
 */
function computeDateToTimestampResult(yearText, monthText, dayText, hourText, minuteText) {
  if (!hasText(yearText) || !hasText(monthText) || !hasText(dayText)) {
    return { kind: "empty" };
  }

  const year = parseNumber(yearText);
  const month = parseNumber(monthText);
  const day = parseNumber(dayText);
  const hour = hasText(hourText) ? parseNumber(hourText) : 0;
  const minute = hasText(minuteText) ? parseNumber(minuteText) : 0;

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return { kind: "error", error: "Please enter valid numbers for date and time fields." };
  }

  const date = new Date(Date.UTC(year, month - 1, day, hour, minute));
  if (Number.isNaN(date.getTime())) {
    return { kind: "error", error: "Invalid date. Please check your inputs." };
  }

  const isExactMatch =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day &&
    date.getUTCHours() === hour &&
    date.getUTCMinutes() === minute;
  if (!isExactMatch) {
    return { kind: "error", error: "Invalid date/time value. Please check your inputs." };
  }

  return {
    kind: "ready",
    timestamp: String(date.getTime() / 1000),
  };
}

/**
 * @param {Date} date
 * @param {string | undefined} timeZone
 * @returns {string}
 */
function formatTimestampDate(date, timeZone) {
  return date.toLocaleString("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZoneName: "short",
  });
}

/**
 * @param {Date} date
 * @returns {string}
 */
function getRelativeTimeLabel(date) {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMs < 0) {
    return "in the future";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;
  }

  if (diffDays < 7) {
    const remainingHours = diffHours % 24;
    if (diffDays === 0) {
      return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    }

    return `${diffDays} day${diffDays === 1 ? "" : "s"} and ${remainingHours} hour${remainingHours === 1 ? "" : "s"} ago`;
  }

  if (diffDays <= 30) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }

  const remainingDays = diffDays % 30;
  return `${diffMonths} month${diffMonths === 1 ? "" : "s"} and ${remainingDays} day${remainingDays === 1 ? "" : "s"} ago`;
}
